import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * Strategy & Management Consultant — one of the five open roles the Careers
 * page lists.
 *
 * Same shape as the other role pages: the mission, the success measures as a
 * list, the responsibilities and the offer as full-width rows, and every
 * "Apply now" pointing at `#contact` — the shared contact band's anchor at the
 * foot of the document — so the button scrolls to the form instead of leaving
 * the page. The source document's emoji headings are set as type here, like
 * every other heading on the site.
 */
export const route: RouteDef = {
  id: 'careersStrategyManagementConsultant',
  path: 'become-a-digitopian/strategy-management-consultant',
  priority: 0.6,
}

export const careersStrategyManagementConsultant: WrittenPage = {
  navLabel: 'Strategy & Management Consultant',
  title: 'Strategy & Management Consultant',
  description:
    'Open role at Digitopia: Strategy & Management Consultant, hybrid in Istanbul. Take leadership teams from a fact-based baseline to strategy, and from strategy to measurable business value.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Careers',
      headline: 'Strategy & Management Consultant',
      sub: 'Location: Hybrid (Istanbul) — Full-time',
      body: [
        'A structured thinker, trusted facilitator and outcome-oriented advisor: combine our maturity frameworks, global benchmarks and the Impact Platform to help leaders move from assessment to strategy, and from strategy to measurable value.',
      ],
      ctas: [{ label: 'Apply now', href: '#apply' }],
    },
    {
      kind: 'prose',
      heading: 'We’re on a mission',
      paragraphs: [
        'Digitopia is reinventing consulting. We help ambitious organizations #ScaleValue. As creators of the Impact Platform and maturity frameworks, we enable companies to understand where they stand, decide what matters next, and turn digital and AI ambition into measurable progress.',
        'Transformation succeeds when leadership teams share a fact-based view of where they stand, make clear choices about where to go, and translate those choices into an executable roadmap.',
        'To help organizations do exactly that, we are looking for a Strategy & Management Consultant: a structured thinker, trusted facilitator and outcome-oriented advisor. You will combine Digitopia’s maturity frameworks, global benchmarks, consulting expertise and Impact Platform to help leaders move from assessment to strategy and from strategy to measurable business value.',
      ],
    },
    {
      kind: 'list',
      heading: 'What success looks like',
      intro: 'You will create clarity where transformation is complex. Success means:',
      items: [
        'Helping leadership teams align on a credible, data-driven baseline.',
        'Turning maturity gaps and strategic ambition into focused priorities and executable roadmaps.',
        'Producing recommendations that are rigorous, practical and connected to business value.',
        'Building trusted relationships that lead to sustained impact, not one-off presentations.',
      ],
    },
    {
      kind: 'features',
      variant: 'rows',
      heading: 'What you’ll do',
      items: [
        {
          title: 'Frame the real question',
          body: 'Work with senior stakeholders to clarify objectives, decision needs, scope, and the business outcomes a transformation must create.',
        },
        {
          title: 'Lead maturity assessments',
          body: 'Use Digitopia’s Digital & AI Maturity Index and related frameworks to guide evidence collection, interviews, workshops and analysis.',
        },
        {
          title: 'Turn data into insight',
          body: 'Interpret assessment results, benchmarks, qualitative evidence and business context to identify the few gaps and opportunities that matter most.',
        },
        {
          title: 'Shape strategy and roadmaps',
          body: 'Develop target states, strategic choices, capability priorities, transformation themes, initiative portfolios and sequenced roadmaps.',
        },
        {
          title: 'Facilitate executive alignment',
          body: 'Design and lead workshops that surface trade-offs, build shared conviction, and convert discussion into decisions.',
        },
        {
          title: 'Connect strategy to value',
          body: 'Define business cases, KPIs, outcomes and governance principles that make progress and impact measurable.',
        },
        {
          title: 'Use the Impact Platform',
          body: 'Translate consulting work into a transparent digital experience that clients can use beyond the engagement.',
        },
        {
          title: 'Contribute to growth',
          body: 'Support proposals, executive conversations, solution design and thought leadership with credible points of view.',
        },
        {
          title: 'Strengthen our intellectual property',
          body: 'Turn project learning into reusable methods, benchmarks, playbooks and product improvements.',
        },
      ],
    },
    {
      kind: 'list',
      heading: 'What makes you a great fit',
      items: [
        'You have hands-on experience in at least one transformation discipline, such as digital or AI strategy, operating model design, innovation, customer experience, data, sustainability or organizational change.',
        'You bring strong analytical skills and have used methods such as business cases, financial modelling, benchmarking, survey analysis or data visualization to support recommendations.',
        'You have worked in a consulting firm, corporate strategy team, enterprise transformation program, or another complex cross-functional environment.',
        'You can create clear, executive-ready materials and contribute confidently to senior leadership and C-suite workshops.',
        'You can structure an ambiguous problem, identify the critical questions, and build a fact base before jumping to an answer.',
        'You facilitate discussions confidently, listen carefully, and know how to challenge constructively.',
        'You understand that transformation is broader than technology and includes leadership, governance, people, operations, customers, innovation, data and business models.',
        'You manage your work with discipline and can balance quality, pace and stakeholder expectations.',
        'You are curious across industries and quickly learn how different organizations create value.',
        'You are comfortable working with clients and traveling when an engagement requires it.',
        'You are fluent in English. An additional language is a plus.',
      ],
    },
    {
      kind: 'features',
      variant: 'rows',
      heading: 'What’s in it for you',
      items: [
        {
          title: 'Executive-level impact',
          body: 'Work on questions that shape how organizations prioritize and govern major digital and AI investments.',
        },
        {
          title: 'A differentiated consulting model',
          body: 'Combine advisory work with proprietary frameworks, global benchmarks, and a platform that keeps strategy connected to execution.',
        },
        {
          title: 'Broad exposure',
          body: 'Learn across industries, functions, transformation domains and geographies.',
        },
        {
          title: 'Visible ownership',
          body: 'Take responsibility for meaningful workstreams and grow toward leading client engagements.',
        },
        {
          title: 'Continuous growth',
          body: 'Access mentorship, courses, project feedback and internal knowledge-sharing.',
        },
        {
          title: 'Balanced flexibility',
          body: 'Enjoy hybrid working, flexible hours and generous paid time off.',
        },
        {
          title: 'Supportive culture',
          body: 'Join a team that values authenticity, curiosity, bold ideas and learning through action.',
        },
        {
          title: 'Long-term rewards',
          body: 'Receive a highly competitive compensation package with stock options tied to your lasting impact.',
        },
      ],
    },
    {
      kind: 'prose',
      align: 'center',
      heading:
        'Ready to help leaders turn transformation ambition into clear choices, aligned roadmaps and measurable value?',
      sub: 'Apply now. We would love to meet you if you are ready to think rigorously, communicate clearly, and make strategy useful.',
      cta: { label: 'Apply now', href: '#apply' },
    },
    {
      kind: 'apply',
      opening: 'strategy-management-consultant',
      heading: 'Apply for Strategy & Management Consultant',
      sub: 'Hybrid (Istanbul), full-time. Tell us who you are and attach your CV; we read every application and reply either way.',
    },
  ],
}
